import React, { useCallback, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';

import { Tracking } from 'bdvp-tracking';

import { pageTemplate } from '../../styles/theme';
import { getMediaPath } from '../../utils';
import { CarouselRowType, CardType } from '../../utils/constants';

import Card from '../Card';
import Carousel, { CAROUSEL_ITEM_PADDING } from '../Carousel';
import Title from '../RowHeading';

import { CarouselRowStyling } from './constants';

const CarouselRow = forwardRef(({
    className,
    title,
    list,
    hasMore,
    fetchMoreData,
    isLoading,
    isLoadingMore,
    isSearchData,
    limit,
    link,
    searchParams,
    rank,
    onItemClickHistoryPushState,
    type,
}, ref) => {

    const history = useHistory();

    const onItemClickHandler = useCallback((event, item, index) => {

        event.preventDefault();
        const mediaPath = getMediaPath(item);
        const search = isSearchData ? `${searchParams}&p=${Math.floor(index / limit)}` : searchParams;

        history.push(
            {
                pathname: `/${mediaPath}`,
                search,
            },
            onItemClickHistoryPushState
        );

        Tracking.trackCarouselOnItemClick({
            carouselRank: rank,
            carouselTitle: title,
            index,
            id: item.slug,
            carouselType: CarouselRowType[type],
        });

    }, [
        history,
        isSearchData,
        limit,
        searchParams,
        rank,
        title,
        onItemClickHistoryPushState,
        type,
    ]);

    const onAfterChangeHandler = useCallback((isScrollLeft) => {

        Tracking.trackCarouselScroll({
            carouselRank: rank,
            carouselTitle: title,
            isScrollLeft,
            carouselType: CarouselRowType[type],
        });

    }, [
        rank,
        title,
        type,
    ]);

    const {
        PARTIAL_VISIBILITY_GUTTER,
        LAYOUT_ITEMS,
    } = CarouselRowStyling[type];

    const imgSizes = [
        `(min-width: ${BREAKPOINTS.xl}) calc(((100vw - ${(parseFloat(pageTemplate.paddingLeftRight.lg) - CAROUSEL_ITEM_PADDING.xl) * 2}px) / ${LAYOUT_ITEMS.xl}) - ${CAROUSEL_ITEM_PADDING.xl * 2}px),`,
        `(min-width: ${BREAKPOINTS.lg}) calc(((100vw - ${(parseFloat(pageTemplate.paddingLeftRight.lg) - CAROUSEL_ITEM_PADDING.lg) * 2}px) / ${LAYOUT_ITEMS.lg}) - ${CAROUSEL_ITEM_PADDING.lg * 2}px),`,
        `(min-width: ${BREAKPOINTS.md}) calc(((100vw - ${((parseFloat(pageTemplate.paddingLeftRight.md) - CAROUSEL_ITEM_PADDING.md) * 2) + PARTIAL_VISIBILITY_GUTTER.md}px) / ${LAYOUT_ITEMS.md}) - ${CAROUSEL_ITEM_PADDING.md * 2}px),`,
        `(min-width: ${BREAKPOINTS.sm}) calc(((100vw - ${((parseFloat(pageTemplate.paddingLeftRight.xs) - CAROUSEL_ITEM_PADDING.sm) * 2) + PARTIAL_VISIBILITY_GUTTER.sm}px) / ${LAYOUT_ITEMS.sm}) - ${CAROUSEL_ITEM_PADDING.sm * 2}px),`,
        `calc(100vw - ${((parseFloat(pageTemplate.paddingLeftRight.xs) - CAROUSEL_ITEM_PADDING.xs) * 2) + PARTIAL_VISIBILITY_GUTTER.xs + (CAROUSEL_ITEM_PADDING.xs * 2)}px)`,
    ].join('');

    const renderItem = useCallback((item, isLoading, index = 0) => (
        <Card
            isLoading={isLoading}
            mediaPlayback={item}
            onClick={(e) => onItemClickHandler(e, item, index)}
            orientation={'portrait'}
            searchParams={searchParams}
            popoverPlacement={'left'}
            imgSizes={imgSizes}
            type={
                type === CarouselRowType.FEATURED
                    ? CardType.HERO
                    : CardType.CONTENT
            }
        />
    ), [
        searchParams,
        onItemClickHandler,
        type,
        imgSizes,
    ]);

    const handleClick = useCallback(() => Tracking.trackCarouselTitleClick({
        carouselRank: rank,
        carouselTitle: title,
        carouselType: CarouselRowType[type],
    }), [
        rank,
        title,
        type,
    ]);

    if (list.length === 0 && !isLoading) {

        return null;

    }

    return (
        <div ref={ref} className={className}>
            <Title
                loading={isLoading}
                text={title}
                link={link}
                onClick={handleClick}
                enableLeftRightMargin
            />
            <Carousel
                hasMore={hasMore}
                isLoading={isLoading}
                isLoadingMore={isLoadingMore}
                limit={limit}
                list={list}
                fetchMoreData={fetchMoreData}
                renderItem={renderItem}
                onAfterChange={onAfterChangeHandler}
                partialVisibilityGutter={PARTIAL_VISIBILITY_GUTTER}
                layoutItems={LAYOUT_ITEMS}
            />
        </div>
    );

});

CarouselRow.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    list: PropTypes.array.isRequired,
    rank: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    fetchMoreData: PropTypes.func,
    hasMore: PropTypes.bool,
    isLoadingMore: PropTypes.bool,
    isSearchData: PropTypes.bool,
    limit: PropTypes.number,
    link: PropTypes.string,
    onItemClickHistoryPushState: PropTypes.object,
    searchParams: PropTypes.string,
    type: PropTypes.string,
};

CarouselRow.defaultProps = {
    fetchMoreData: () => {},
    hasMore: false,
    isLoadingMore: false,
    isSearchData: false,
    searchParams: '',
    onItemClickHistoryPushState: undefined,
    type: CarouselRowType.GENERIC,
};

export default CarouselRow;
